import { useEffect, useState } from "react";
import { api, mediaUrl, fetchIngredientImages, fetchUtensilImages } from "@/lib/api";
import { fmt } from "@/lib/food";
import { toast } from "sonner";
import { Copy, Check, Plus, X, Home, Trash2, ImageIcon } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const placeholderTile = (
  <div className="shrink-0 w-14 h-14 rounded-2xl bg-nerd-lift flex items-center justify-center">
    <ImageIcon className="w-6 h-6 text-nerd-light/25" />
  </div>
);
const imgTile = (url, alt) => (
  <div className="shrink-0 w-14 h-14 rounded-2xl overflow-hidden bg-nerd-lift">
    <img src={mediaUrl(url)} alt={alt} className="w-full h-full object-cover" />
  </div>
);

function SupplyCard({ testid, tile, title, sub, checked, onToggle, onRemove, onDelete }) {
  return (
    <div data-testid={testid}
      className={`w-full flex items-center gap-4 p-3 rounded-2xl border transition-colors ${checked ? "bg-nerd-lift border-black/5" : "bg-nerd-card border-black/8"}`}>
      {tile}
      <div className="flex-1 min-w-0">
        <p className={`font-medium truncate ${checked ? "line-through text-nerd-light/40" : "text-nerd-light"}`}>{title}</p>
        {sub && <p className={`text-sm ${checked ? "text-nerd-light/25" : "text-nerd-light/50"}`}>{sub}</p>}
      </div>
      {onRemove ? (
        <button onClick={onRemove} className="shrink-0 w-8 h-8 rounded-full border border-black/15 flex items-center justify-center text-nerd-light/50 hover:bg-nerd-light hover:text-white hover:border-nerd-light transition-colors">
          <X className="w-4 h-4" />
        </button>
      ) : (
        <div className="shrink-0 flex items-center gap-2">
          {onDelete && (
            <button onClick={onDelete} title="Remove custom item"
              className="w-8 h-8 rounded-full border border-black/15 flex items-center justify-center text-nerd-light/50 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button onClick={onToggle} className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${checked ? "bg-nerd-light border-2 border-nerd-light text-white" : "border-2 border-nerd-light/50 text-transparent"}`}>
            <Check className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>
      )}
    </div>
  );
}

export default function Supplies() {
  const [supplies, setSupplies] = useState(null);
  const [pantry, setPantry] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState("");
  const [newShopItem, setNewShopItem] = useState("");
  const [ingImages, setIngImages] = useState({});
  const [utImages, setUtImages] = useState({});

  const loadPantry = () => api.get("/pantry").then((r) => setPantry(r.data.items || []));
  const loadSupplies = () => api.get("/mealplan/shopping-list").then((r) => setSupplies(r.data));

  useEffect(() => {
    Promise.all([
      api.get("/mealplan/shopping-list").then((r) => setSupplies(r.data)),
      loadPantry(),
    ]).catch(() => toast.error("Could not load supplies")).finally(() => setLoading(false));
  }, []);

  // Fetch AI-generated images for the ingredient/utensil cards (same as recipe view)
  useEffect(() => {
    const ingNames = new Set();
    (supplies?.aisles || []).forEach((a) => a.items.forEach((it) => it.name && ingNames.add(it.name)));
    pantry.forEach((p) => p.name && ingNames.add(p.name));
    const list = [...ingNames];
    if (list.length) fetchIngredientImages(list).then((m) => setIngImages((prev) => ({ ...prev, ...m }))).catch(() => {});
    const uts = (supplies?.utensils || []).map((u) => u.name).filter(Boolean);
    if (uts.length) fetchUtensilImages(uts).then((m) => setUtImages((prev) => ({ ...prev, ...m }))).catch(() => {});
  }, [supplies, pantry]);

  const ingTile = (name) => {
    const url = ingImages[(name || "").trim().toLowerCase()];
    return url ? imgTile(url, name) : placeholderTile;
  };
  const utTile = (name) => {
    const url = utImages[(name || "").trim().toLowerCase()];
    return url ? imgTile(url, name) : placeholderTile;
  };

  const inPantry = (name) => pantry.some((p) => p.name.trim().toLowerCase() === name.trim().toLowerCase());

  const toggleShopItem = async (it) => {
    const have = inPantry(it.name);
    // optimistic
    if (have) {
      setPantry((p) => p.filter((x) => x.name.trim().toLowerCase() !== it.name.trim().toLowerCase()));
      await api.delete(`/pantry/by-name/${encodeURIComponent(it.name)}`).catch(() => {});
    } else {
      const optimistic = { id: `tmp-${it.name}`, name: it.name, quantity: it.quantity, unit: it.unit, aisle: it.aisle };
      setPantry((p) => [...p, optimistic]);
      await api.post("/pantry/add", { name: it.name, quantity: it.quantity, unit: it.unit, aisle: it.aisle }).catch(() => {});
      loadPantry();
    }
  };

  const addManual = async () => {
    const name = newItem.trim();
    if (!name) return;
    setNewItem("");
    await api.post("/pantry/add", { name, aisle: "Other" }).catch(() => toast.error("Could not add"));
    loadPantry();
    toast.success(`${name} added to pantry`);
  };

  const removePantry = async (item) => {
    setPantry((p) => p.filter((x) => x.id !== item.id));
    await api.delete(`/pantry/item/${item.id}`).catch(() => {});
  };

  const addCustomShop = async () => {
    const name = newShopItem.trim();
    if (!name) return;
    setNewShopItem("");
    await api.post("/shopping/custom", { name, aisle: "Other" }).catch(() => toast.error("Could not add"));
    await loadSupplies();
    toast.success(`${name} added to your list`);
  };

  const removeCustomShop = async (item) => {
    await api.delete(`/shopping/custom/${item.item_id}`).catch(() => {});
    await loadSupplies();
  };

  const utensils = supplies?.utensils || [];
  // hide items already in pantry from the "to buy" count
  const toBuyCount = supplies ? supplies.aisles.reduce((n, a) => n + a.items.filter((i) => !inPantry(i.name)).length, 0) : 0;

  const copyList = () => {
    if (!supplies) return;
    const txt = supplies.aisles.map((a) => {
      const rows = a.items.filter((i) => !inPantry(i.name)).map((i) => `  - ${i.name} — ${fmt(i.quantity)} ${i.unit}`);
      return rows.length ? `${a.aisle}\n${rows.join("\n")}` : "";
    }).filter(Boolean).join("\n\n");
    navigator.clipboard.writeText(txt);
    toast.success("Shopping list copied");
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Supplies</h1>
      </div>

      {loading ? (
        <div className="py-20 text-center text-nerd-light/40 text-sm">Building your supplies…</div>
      ) : (
        <Tabs defaultValue="shopping">
          <TabsList className="w-full grid grid-cols-3 bg-transparent border-b border-black/10 rounded-none p-0 h-auto mb-6">
            {[["shopping", "Shopping List"], ["pantry", "Pantry"], ["utensils", "Utensils"]].map(([v, l]) => (
              <TabsTrigger key={v} data-testid={`tab-${v}`} value={v}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-nerd-light data-[state=active]:bg-transparent data-[state=active]:text-nerd-light data-[state=active]:shadow-none text-nerd-light/40 pb-3 font-display font-bold text-base">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Shopping list */}
          <TabsContent value="shopping">
            <div className="flex gap-2 mb-6">
              <input data-testid="shop-add-input" value={newShopItem} onChange={(e) => setNewShopItem(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addCustomShop(); }}
                placeholder="Add a custom item… e.g. Paper towels"
                className="flex-1 bg-nerd-card border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-nerd-light transition-colors" />
              <button data-testid="shop-add-btn" onClick={addCustomShop}
                className="shrink-0 inline-flex items-center gap-1.5 bg-nerd-light text-white px-4 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            {supplies && supplies.total_items > 0 ? (
              <div className="space-y-6">
                <div className="rounded-2xl bg-nerd-card border border-black/8 p-5">
                  <p className="text-nerd-light/50 text-xs font-mono-x uppercase tracking-widest">To buy</p>
                  <p className="font-display text-5xl font-extrabold mt-1">{toBuyCount}</p>
                  <p className="text-nerd-light/40 text-xs mt-1">Tick an item once you have it — it moves to your Pantry.</p>
                </div>
                <button data-testid="copy-list-btn" onClick={copyList}
                  className="w-full inline-flex items-center justify-center gap-2 bg-nerd-lift hover:bg-black/10 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors">
                  <Copy className="w-4 h-4" /> Copy list
                </button>
                {supplies.aisles.map((aisle) => (
                  <div key={aisle.aisle}>
                    <h4 className="font-display text-xl font-bold mb-3">{aisle.aisle}</h4>
                    <div className="space-y-2.5">
                      {aisle.items.map((it, i) => {
                        return (
                          <SupplyCard key={i} testid={`shop-item-${aisle.aisle}-${it.name}`}
                            tile={ingTile(it.name)}
                            title={it.name} sub={it.custom ? (it.quantity ? `${fmt(it.quantity)} ${it.unit}`.trim() : "Custom item") : `${fmt(it.quantity)} ${it.unit}`.trim()}
                            checked={inPantry(it.name)} onToggle={() => toggleShopItem(it)}
                            onDelete={it.custom ? () => removeCustomShop(it) : undefined} />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-20 text-center text-nerd-light/40 text-sm">Your shopping list is empty.</div>
            )}
          </TabsContent>

          {/* Pantry */}
          <TabsContent value="pantry">
            <div className="space-y-6">
              <div className="rounded-2xl bg-nerd-card border border-black/8 p-5">
                <p className="text-nerd-light/50 text-xs font-mono-x uppercase tracking-widest">In your pantry</p>
                <p className="font-display text-5xl font-extrabold mt-1">{pantry.length}</p>
                <p className="text-nerd-light/40 text-xs mt-1">Things you already have at home.</p>
              </div>

              <div className="flex gap-2">
                <input data-testid="pantry-add-input" value={newItem} onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addManual(); }}
                  placeholder="Add something you have… e.g. Olive oil"
                  className="flex-1 bg-nerd-card border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-nerd-light transition-colors" />
                <button data-testid="pantry-add-btn" onClick={addManual}
                  className="shrink-0 inline-flex items-center gap-1.5 bg-nerd-light text-white px-4 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity">
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>

              {pantry.length > 0 ? (
                <div className="space-y-2.5" data-testid="pantry-list">
                  {pantry.map((p) => {
                    return (
                      <SupplyCard key={p.id} testid={`pantry-item-${p.name}`}
                        tile={ingTile(p.name)}
                        title={p.name} sub={p.quantity ? `${fmt(p.quantity)} ${p.unit}`.trim() : "Available"}
                        onRemove={() => removePantry(p)} />
                    );
                  })}
                </div>
              ) : (
                <div className="py-16 text-center text-nerd-light/40 text-sm flex flex-col items-center gap-3">
                  <Home className="w-8 h-8 opacity-40" />
                  Your pantry is empty. Add items above, or tick them off the shopping list.
                </div>
              )}
            </div>
          </TabsContent>

          {/* Utensils */}
          <TabsContent value="utensils">
            {utensils.length > 0 ? (
              <div className="space-y-6">
                <div className="rounded-2xl bg-nerd-card border border-black/8 p-5">
                  <p className="text-nerd-light/50 text-xs font-mono-x uppercase tracking-widest">You'll need</p>
                  <p className="font-display text-5xl font-extrabold mt-1">{utensils.length}</p>
                </div>
                <div className="space-y-2.5">
                  {utensils.map((u, i) => (
                    <SupplyCard key={i} testid={`supply-utensil-${i}`}
                      tile={utTile(u.name)}
                      title={u.name} sub={(u.recipes || [])[0]}
                      checked={inPantry(u.name)} onToggle={() => toggleShopItem({ name: u.name, quantity: 0, unit: "", aisle: "Other" })} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-20 text-center text-nerd-light/40 text-sm">No utensils needed yet.</div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
